import React, { useState, useEffect, ChangeEvent } from "react";
import { storage } from "config/firebase";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";

import {
  Avatar,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  Paper,
  IconButton,
  Skeleton,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import UploadIcon from "@mui/icons-material/Upload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import placeholder from "images/profile-placeholder.avif";
import { useSelector } from "react-redux";
import { RootState } from "types";

const ProfileAvatarContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + "08",
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 200,
  height: 200,
  marginBottom: theme.spacing(2),
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[3],
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.02)",
  },
}));

const HiddenInput = styled("input")({
  display: "none",
});

function UploadImage() {
  const user = useSelector((state: RootState) => state.user.user);

  const [previewImg, setPreviewImg] = useState(placeholder);
  const [showUploadInput, setShowUploadInput] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const profilePictureRef = ref(storage, `profilePictures/${user.uid}`);

  useEffect(() => {
    getProfilePicture();
  }, []);

  const getProfilePicture = async () => {
    try {
      setLoading(true);
      const url = await getDownloadURL(profilePictureRef);
      setPreviewImg(url);
    } catch (err) {
      // User likely doesn't have a profile picture yet
      setPreviewImg(placeholder);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setSuccess(null);
    const file = event.target.files?.[0];

    if (file && checkFileSize(file) && checkMimeType(file)) {
      setPreviewImg(URL.createObjectURL(file));
      setImage(file);
      setShowUploadInput(false);
    }
  };

  const checkFileSize = (file: File): boolean => {
    const maxSize = 2000000; // 2MB

    if (file.size > maxSize) {
      setError("Image too large. Please select a file smaller than 2MB.");
      return false;
    }
    return true;
  };

  const checkMimeType = (file: File): boolean => {
    const allowedTypes = ["image/png", "image/jpeg", "image/gif"];

    if (!allowedTypes.includes(file.type)) {
      setError(
        "Invalid file type. Only PNG, JPEG, and GIF files are supported.",
      );
      return false;
    }

    return true;
  };

  const uploadPic = async () => {
    if (!image) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const profilePictureRef = ref(storage, `profilePictures/${user.uid}`);
      await uploadBytes(profilePictureRef, image);

      setImage(null);
      setSuccess("Profile picture updated successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setImage(null);
    setError(null);
    setSuccess(null);
    setShowUploadInput(false);
    getProfilePicture(); // Reset to current profile picture
  };

  if (loading) {
    return (
      <ProfileAvatarContainer elevation={0}>
        <Skeleton variant="circular" width={200} height={200} />
        <Skeleton variant="text" width={150} height={30} sx={{ mt: 2 }} />
        <Skeleton
          variant="rectangular"
          width={120}
          height={36}
          sx={{ mt: 1 }}
        />
      </ProfileAvatarContainer>
    );
  }

  return (
    <Box>
      <ProfileAvatarContainer elevation={0}>
        <StyledAvatar alt="Profile Picture" src={previewImg} />

        <Typography variant="h6" gutterBottom color="text.secondary">
          Profile Picture
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, width: "100%" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2, width: "100%" }}
            onClose={() => setSuccess(null)}
            icon={<CheckCircleIcon />}
          >
            {success}
          </Alert>
        )}

        {uploading && (
          <Box sx={{ width: "100%", mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Uploading image...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        <Stack direction="row" spacing={2} alignItems="center">
          {!showUploadInput && !image && (
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={() => setShowUploadInput(true)}
              disabled={uploading}
            >
              Change Picture
            </Button>
          )}

          {showUploadInput && (
            <>
              <Button
                variant="contained"
                component="label"
                startIcon={<PhotoCameraIcon />}
                disabled={uploading}
              >
                Select Image
                <HiddenInput
                  type="file"
                  onChange={handleChange}
                  accept="image/png,image/jpeg,image/gif"
                />
              </Button>
              <Button
                variant="text"
                onClick={() => setShowUploadInput(false)}
                disabled={uploading}
              >
                Cancel
              </Button>
            </>
          )}

          {image && (
            <>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={uploadPic}
                disabled={uploading}
                color="success"
              >
                Upload
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancelUpload}
                disabled={uploading}
              >
                Cancel
              </Button>
            </>
          )}
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, textAlign: "center" }}
        >
          Supported formats: PNG, JPEG, GIF • Max size: 2MB
        </Typography>
      </ProfileAvatarContainer>
    </Box>
  );
}

export default UploadImage;
